import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Group, Select, Stack, Text } from '@mantine/core'

import { DataCard } from '@/components/ui'
import { useGravarPublicacoes, usePublicacoesDoRobo } from '@/api/robo'

/**
 * "Avisar o grupo" (spec 2026-09-30-robo-agente-pessoal §5.3). O dono escolhe um
 * grupo de Organização de que participa e os ambientes que o robô acompanha; quando
 * houver crítico num deles, o robô avisa ali. Salvar troca só os ambientes do grupo
 * escolhido e mantém o que estiver ligado nos outros.
 */
export function PublicacoesDoRobo({ nome }: { nome: string }) {
  const { data, isError } = usePublicacoesDoRobo()
  const gravar = useGravarPublicacoes()
  const itens = data?.itens ?? []
  const grupos = data?.grupos ?? []
  const ambientes = data?.tenants ?? []
  const [grupo, setGrupo] = useState<string | null>(null)
  const [marcados, setMarcados] = useState<string[]>([])
  const [salvo, setSalvo] = useState(false)
  const [falhou, setFalhou] = useState(false)

  // O grupo inicial é o primeiro que já tem algo ligado; sem nada, o primeiro da lista.
  const inicial = String(itens[0]?.conversation_id ?? grupos[0]?.conversation_id ?? '')
  useEffect(() => { if (grupo === null && inicial) setGrupo(inicial) }, [grupo, inicial])

  // Sincroniza pelo VALOR (grupo + lista gravada), não pela identidade de `data`.
  const gravadosDoGrupo = itens
    .filter((i) => String(i.conversation_id) === grupo)
    .map((i) => String(i.tenant_id))
    .join('\n')
  useEffect(() => {
    setMarcados(gravadosDoGrupo ? gravadosDoGrupo.split('\n') : [])
  }, [gravadosDoGrupo])

  const salvar = async () => {
    if (grupo === null) return
    const outros = itens.filter((i) => String(i.conversation_id) !== grupo)
    const doGrupo = marcados.map((t) => ({ conversation_id: Number(grupo), tenant_id: Number(t) }))
    setFalhou(false)
    try {
      await gravar.mutateAsync({ itens: [...outros, ...doGrupo] })
      setSalvo(true)
    } catch {
      setFalhou(true)
    }
  }

  return (
    <DataCard title="Avisar o grupo">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Quando houver alerta crítico nestes ambientes, o {nome} avisa o grupo. Se alguém do grupo
          não enxerga o ambiente, ele só diz que há um alerta, sem o detalhe.
        </Text>
        {isError && <Alert color="red" variant="light">Não consegui ler a lista agora.</Alert>}
        {data && grupos.length === 0 && (
          <Text size="sm">Você não participa de nenhum grupo de Organização.</Text>
        )}
        {data && grupos.length > 0 && ambientes.length === 0 && (
          <Text size="sm">O {nome} ainda não acompanha nenhum ambiente. Escolha em “De onde vêm os alertas”.</Text>
        )}
        {grupos.length > 0 && ambientes.length > 0 && (
          <>
            <Select
              label="Grupo"
              data={grupos.map((g) => ({ value: String(g.conversation_id), label: g.titulo || `Grupo ${g.conversation_id}` }))}
              value={grupo}
              onChange={(v) => { setGrupo(v); setSalvo(false) }}
              allowDeselect={false}
            />
            <Checkbox.Group label="Ambientes" value={marcados}
              onChange={(v) => { setMarcados(v); setSalvo(false) }}>
              <Stack gap="xs" mt={4}>
                {ambientes.map((a) => (
                  <Checkbox key={a.tenant_id} value={String(a.tenant_id)} label={a.nome} />
                ))}
              </Stack>
            </Checkbox.Group>
            <Group>
              <Button loading={gravar.isPending} onClick={salvar}>Salvar</Button>
              {salvo && <Text size="sm" c="dimmed">Salvo.</Text>}
              {falhou && <Text size="sm" c="red">Não consegui salvar. Recarregue a página e tente de novo.</Text>}
            </Group>
          </>
        )}
      </Stack>
    </DataCard>
  )
}
